/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { AppProviders } from '@/app/providers';
import { App } from '@/app/App';
import { registerAuthBridge } from '@/auth/authBridge';
import { initializeMsal } from '@/auth/msalConfig';
import { setAuthErrorHandler } from '@/lib/apiClient';
import { handleApiAuthError } from '@/lib/authError';
import { installStaleChunkReload, router } from '@/routes/router';
import '@/i18n/i18n';
import '@/styles/index.css';
import '@/styles/motion.css';

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root container #root was not found in the document.');
}

// Recover automatically when a deploy swaps out the hashed chunk filenames an open tab still
// references (otherwise a lazy route or a preloaded CSS dep 404s and the app hard-errors).
installStaleChunkReload();

// Wire the token/claims bridge before the first render so no boot request races ahead of the
// token (an effect would register too late; see registerAuthBridge).
registerAuthBridge();

// Auth failures from the API are handled centrally: a 401 (no/expired session) routes to the access
// page to re-authenticate, while a 403 fails soft (logged for diagnosis) unless the token positively
// lacks any eDiag role - so one forbidden call (e.g. a dealer's internal-only program-search) never
// bounces a valid user out. See handleApiAuthError.
setAuthErrorHandler((status, error) => {
  handleApiAuthError(status, error, {
    navigate: (to) => void router.navigate(to),
    currentLocation: () => router.state.location,
  });
});

// After an MSAL redirect, some IdP configurations (e.g. a redirect URI registered as "Web"
// instead of "Single-page application") leave the auth response params (?code, ?state, ...) in
// the address bar, so the URL looks "stuck". handleRedirectPromise (awaited in initializeMsal)
// has already consumed whatever it can, so strip the leftovers to restore a clean URL.
function stripAuthResponseParams(): void {
  const url = new URL(window.location.href);
  const junk = ['code', 'state', 'session_state', 'client_info', 'error', 'error_description'];
  let changed = false;
  for (const key of junk) {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  }
  if (changed) {
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`,
    );
  }
}

// MSAL must init before render (handles redirect response).
void initializeMsal().finally(() => {
  stripAuthResponseParams();
  createRoot(container).render(
    <StrictMode>
      <AppProviders>
        <App />
      </AppProviders>
    </StrictMode>,
  );
});
